import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Frontend error tracking (Sentry, M7 36a). Config-gated on `VITE_SENTRY_DSN`: a NO-OP when unset
 * (the dev/test/CI default) — nothing initializes and nothing is sent. Kept LAZY: the `@sentry/react`
 * SDK is dynamically imported only when a DSN is present, so it never inflates the kid-path bundle.
 *
 * Kids Home is a CHILDREN'S product — `beforeSend` ({@link scrubBrowserEvent}) strips PII from the
 * STRUCTURED fields before any event leaves the device: the Sentry identity block, request/breadcrumb
 * data, PII-keyed `extra`, and every URL's query/hash (route ids are opaque UUIDs and safe, but
 * query/state may carry free text). Pathnames are NOT uniformly safe: the invite/reset/verify flows
 * carry a live single-use credential IN the path, so {@link stripUrl} redacts those token segments
 * too. It does NOT rewrite free-text `message`/`exception` strings, so
 * thrown errors must never interpolate PII (display names, emails, tokens) into their text. The DSN is a
 * public ingest key (safe to embed in the client bundle), not a secret.
 */

/** Substrings marking a breadcrumb/extra key as PII or secret-bearing (case-insensitive). */
const PII_KEY_SUBSTRINGS: readonly string[] = [
  'password',
  'pin',
  'token',
  'secret',
  'authorization',
  'cookie',
  'csrf',
  'email',
  'name', // displayName / username / householdName …
  'phone',
  'address',
  'avatar',
  'endpoint',
];

function isPiiKey(key: string): boolean {
  const lower = key.toLowerCase();
  return PII_KEY_SUBSTRINGS.some((needle) => lower.includes(needle));
}

/**
 * Pathnames whose path SEGMENT is a live single-use credential (invite/reset/verify tokens), not an
 * opaque route id — the web routes and their backing auth API endpoints. The token segment is
 * replaced with a placeholder; any suffix (e.g. `/accept`) is kept. The API pattern is unanchored so
 * it survives an absolute API origin or base-path prefix.
 */
const TOKEN_PATH_PATTERNS: readonly RegExp[] = [
  /^(\/(?:accept-invite|reset-password|verify-email)\/)[^/]+/,
  /(\/v1\/auth\/(?:invitations|password-reset|verify-email)\/)[^/]+/,
];

function redactTokenSegments(pathname: string): string {
  return TOKEN_PATH_PATTERNS.reduce(
    (out, pattern) => out.replace(pattern, '$1[redacted]'),
    pathname,
  );
}

/**
 * Keep only the path of a URL — drop origin, query, and hash (they may carry free text/state) — and
 * redact secret token path segments ({@link TOKEN_PATH_PATTERNS}).
 */
function stripUrl(url: string): string {
  try {
    const u = new URL(url, 'http://local');
    return redactTokenSegments(u.pathname);
  } catch {
    const cut = url.search(/[?#]/);
    return redactTokenSegments(cut === -1 ? url : url.slice(0, cut));
  }
}

function scrubRecord(
  record: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!record) {
    return undefined;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!isPiiKey(key)) {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Sentry `beforeSend` for the browser SDK. Exported standalone so it is unit-testable. Strips the
 * Sentry identity block, scrubs URLs (request + breadcrumbs) of query/state, and drops PII-keyed
 * `extra`/breadcrumb data. Returns the scrubbed event (never drops a genuine error).
 */
export function scrubBrowserEvent(event: ErrorEvent, _hint?: EventHint): ErrorEvent | null {
  delete event.user;
  delete event.server_name;

  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = stripUrl(event.request.url);
    }
    delete event.request.cookies;
    delete event.request.query_string;
    delete event.request.data;
    delete event.request.headers;
  }

  const extra = scrubRecord(event.extra);
  if (extra) {
    event.extra = extra;
  } else {
    delete event.extra;
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      const copy = { ...crumb };
      // A navigation/fetch breadcrumb carries a URL in `data.url`/`data.from`/`data.to` — strip it.
      const data = scrubRecord(crumb.data);
      if (data) {
        for (const k of ['url', 'from', 'to']) {
          if (typeof data[k] === 'string') {
            data[k] = stripUrl(data[k] as string);
          }
        }
        copy.data = data;
      } else {
        delete copy.data;
      }
      return copy;
    });
  }

  return event;
}

/** Read the optional DSN from the Vite env (typed loosely — the var is optional and may be absent). */
function readDsn(): string | undefined {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const dsn = env.VITE_SENTRY_DSN;
  return typeof dsn === 'string' && dsn.length > 0 ? dsn : undefined;
}

/**
 * Initialize Sentry IFF `VITE_SENTRY_DSN` is set. No-op otherwise (returns immediately). The SDK is
 * imported lazily so it stays out of the main bundle when disabled. Call once at startup, before the
 * router mounts, so early errors are captured. Safe to call under test/dev (no DSN ⇒ does nothing).
 */
export async function initSentry(): Promise<void> {
  const dsn = readDsn();
  if (!dsn) {
    return;
  }
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn,
    environment: env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    ...(env.VITE_SENTRY_RELEASE ? { release: env.VITE_SENTRY_RELEASE } : {}),
    // We send PII deliberately and minimally — never let the SDK auto-attach IP/identity.
    sendDefaultPii: false,
    // Mandatory PII scrub on every outgoing event.
    beforeSend: scrubBrowserEvent,
  });
}

/**
 * Report an error caught by the RR7 route error boundary ({@link RouteError}). Lazily imports the
 * SDK and is a no-op when Sentry was never initialized (no DSN) — `captureException` on an
 * uninitialized client silently does nothing, so this is safe to call unconditionally.
 */
export async function reportRouteError(error: unknown): Promise<void> {
  if (!readDsn()) {
    return;
  }
  const Sentry = await import('@sentry/react');
  Sentry.captureException(error);
}
